import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Trophy, Share2, Plus, Camera, X, MessageCircle, Instagram } from 'lucide-react';
import { buildBragCard } from '../../lib/scoringEngine';
import { computeWinStreak } from '../../lib/localStore';
import { buildStoryCard, dataUrlToBlob, downloadDataUrl } from '../../lib/storyCard';

/**
 * End-of-match Brag Card with photo capture and share-to-WhatsApp /
 * share-to-Instagram-Story flows.
 *
 * Share strategy:
 *  - Web Share API Files level 2 (mobile Chrome/Safari): hand the PNG file to
 *    the OS share sheet → user picks WhatsApp or Instagram from the sheet.
 *  - WhatsApp button: if Web Share isn't available, falls back to wa.me with a
 *    pre-filled text + match URL and offers the image as a download so the
 *    user can attach it manually.
 *  - Instagram button: if Web Share isn't available, downloads the story PNG
 *    and opens instagram.com — Instagram web has no story-upload API so a
 *    manual upload is the only desktop path.
 *  - Both buttons disable while the card is rendering and show a working
 *    spinner so users don't double-tap.
 */
export default function BragCard({ state, onClose, onNewMatch }) {
  const brag = buildBragCard(state);
  const winStreak = computeWinStreak(state.winner === 'A' ? state.sideA : state.sideB);
  const fileInputRef = React.useRef(null);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(null); // 'whatsapp' | 'instagram' | 'text' | null

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhoto(ev.target?.result || null);
    reader.onerror = () => toast.error('Could not read photo');
    reader.readAsDataURL(file);
  };

  // Build the PNG story card once and cache the resulting Blob/File for reuse
  // across both share buttons in the same session.
  const buildShareFile = async () => {
    const dataUrl = await buildStoryCard({
      winnerName: brag.winnerName,
      loserName: brag.loserName,
      scoreLine: brag.scoreLine,
      photoDataUrl: photo,
      winStreak,
      presetName: brag.presetName,
    });
    const blob = dataUrlToBlob(dataUrl);
    const file = new File([blob], `loveall-${brag.winnerName}-${Date.now()}.png`, { type: 'image/png' });
    return { dataUrl, blob, file };
  };

  // Use a single source of truth for the URL: pass it via `url:` to Web Share
  // and DON'T duplicate it in `text:` — otherwise WhatsApp / Telegram render
  // the link twice (once for the text, once for the URL preview card).
  const matchUrl = `${window.location.origin}/`;
  const shareTextNoUrl = `🏆 ${brag.winnerName} won ${brag.scoreLine}! 🏸`;
  // Fallback for raw `wa.me/?text=` links — those don't support a separate
  // url param, so the URL must live inside the text body.
  const shareTextForWaMe = `${shareTextNoUrl}\n${matchUrl}`;

  // --- WhatsApp share -----------------------------------------------------
  const handleShareWhatsApp = async () => {
    setBusy('whatsapp');
    try {
      const { dataUrl, file } = await buildShareFile();
      // Prefer Web Share API with file — on Android Chrome and recent iOS this
      // brings up the share sheet with WhatsApp as one of the targets.
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: shareTextNoUrl, url: matchUrl });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return; // user cancelled
          // fall through to wa.me fallback
        }
      }
      // Desktop / unsupported: download the image so the user can attach it,
      // then open WhatsApp Web with the pre-filled text.
      downloadDataUrl(dataUrl, `loveall-${brag.winnerName}-${Date.now()}.png`);
      window.open(`https://wa.me/?text=${encodeURIComponent(shareTextForWaMe)}`, '_blank');
      toast.success('Story card downloaded — attach it to your WhatsApp chat!');
    } catch {
      toast.error('Could not share to WhatsApp');
    } finally {
      setBusy(null);
    }
  };

  // --- Instagram Story share ---------------------------------------------
  const handleShareInstagram = async () => {
    setBusy('instagram');
    try {
      const { dataUrl, file } = await buildShareFile();
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: shareTextNoUrl, url: matchUrl });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }
      // Fallback: download the 9:16 story image. Instagram has no web upload
      // API, so direct the user to Instagram and tell them what to do.
      downloadDataUrl(dataUrl, `loveall-story-${Date.now()}.png`);
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      window.open(isMobile ? 'instagram://story-camera' : 'https://www.instagram.com/', '_blank');
      toast.success('Story card downloaded — upload it as your Instagram Story!');
    } catch {
      toast.error('Could not share to Instagram');
    } finally {
      setBusy(null);
    }
  };

  // --- Plain text share (any app) ----------------------------------------
  const handleShareText = () => {
    if (navigator.share) {
      navigator.share({ text: shareTextNoUrl, url: matchUrl }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareTextForWaMe)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareTextForWaMe)}`, '_blank');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto" data-testid="brag-card">
        <VisuallyHidden asChild>
          <DialogTitle>Match Complete — {brag.winnerName} wins {brag.scoreLine}</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>Share your win on WhatsApp or start a new match.</DialogDescription>
        </VisuallyHidden>
        <div className="bg-gradient-to-br from-primary via-primary to-secondary p-5 text-center space-y-2">
          <Trophy className="h-10 w-10 mx-auto text-white drop-shadow-lg" />
          <p className="text-white/80 text-[10px] uppercase tracking-widest">Match Complete</p>
          <h2 className="font-heading text-2xl font-extrabold text-white uppercase tracking-tight">{brag.winnerName}</h2>
          <p className="text-white/80 text-sm">wins!</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-center space-y-1">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{brag.title}</p>
            <p className="font-mono text-2xl font-black tabular-nums">{brag.scoreLine}</p>
            <p className="text-[11px] text-muted-foreground">
              {brag.presetName} • {Math.floor(brag.durationSec / 60)}m {brag.durationSec % 60}s
              {winStreak > 0 && <> • <span className="text-orange-500 font-bold">🔥 {winStreak} streak</span></>}
            </p>
          </div>

          {photo ? (
            <div className="relative rounded-xl overflow-hidden border-2 border-primary/30">
              <img src={photo} alt="Winning shot" className="w-full aspect-square object-cover" data-testid="brag-photo-preview" />
              <button
                onClick={() => { setPhoto(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                aria-label="Remove photo"
                data-testid="brag-photo-remove"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-24 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 flex flex-col items-center justify-center gap-1 hover:bg-primary/10 transition-colors"
              data-testid="brag-photo-add"
            >
              <Camera className="h-5 w-5 text-primary" />
              <p className="text-sm font-bold text-primary">Add Winning Photo</p>
              <p className="text-[10px] text-muted-foreground">Take a celebration shot to brag with</p>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoPick}
            className="hidden"
            data-testid="brag-photo-input"
          />

          {/* Primary share row — side-by-side WhatsApp + Instagram */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={handleShareWhatsApp}
              disabled={busy !== null}
              className="h-14 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm flex-col gap-0.5"
              data-testid="brag-share-whatsapp"
            >
              <MessageCircle className="h-5 w-5" />
              {busy === 'whatsapp' ? 'Sharing…' : 'WhatsApp'}
            </Button>
            <Button
              onClick={handleShareInstagram}
              disabled={busy !== null}
              className="h-14 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 hover:opacity-90 text-white font-bold text-sm flex-col gap-0.5"
              data-testid="brag-share-instagram"
            >
              <Instagram className="h-5 w-5" />
              {busy === 'instagram' ? 'Sharing…' : 'Instagram Story'}
            </Button>
          </div>

          <Button onClick={handleShareText} variant="outline" className="w-full h-10" data-testid="brag-share-text">
            <Share2 className="mr-2 h-4 w-4" /> Share Score (text only)
          </Button>

          <Button onClick={onNewMatch} variant="ghost" className="w-full h-10" data-testid="brag-new-match">
            <Plus className="mr-2 h-4 w-4" /> New Match
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
